import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, resolveImage, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function PortfolioSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = (content.items || []).map(item => ({ ...item, ...resolveItemMedia(item, mediaMode, mediaFolders) }));
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'grid';
    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const allLabel = content.all_label || 'Todos';
    const [activeFilter, setActiveFilter] = useState(allLabel);
    const [lbIndex, setLbIndex] = useState(-1);
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);

    if (items.length === 0) return null;

    const categoriesStr = content.categories || allLabel;
    const categories = categoriesStr.split(',').map(c => c.trim()).filter(Boolean);
    const filteredItems = activeFilter === allLabel ? items : items.filter(it => it.category === activeFilter);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cols = config.columns || 3;
    const gridCls = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    // ── Filterable variant ──
    if (variant === 'filterable') {
        const uniqueCats = [...new Set(items.map(it => it.category).filter(Boolean))];
        const filterTabs = [allLabel, ...uniqueCats];
        const filtered = activeFilter === allLabel ? items : items.filter(it => it.category === activeFilter);
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Portfolio'} subtitle={content.subtitle} config={config} s={s} />
                    {uniqueCats.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {filterTabs.map(cat => (
                                <button key={cat} onClick={() => { setActiveFilter(cat); setVisibleCount(INITIAL_ITEMS); }}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === activeFilter ? 'text-white shadow-lg' : ''}`}
                                    style={cat === activeFilter ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>{cat}</button>
                            ))}
                        </div>
                    )}
                    <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                        {filtered.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`${card} overflow-hidden group cursor-pointer transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}
                                onClick={() => item.link ? window.open(item.link, '_blank') : setLbIndex(i)}>
                                <div className="aspect-[4/3] overflow-hidden">
                                    <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                                </div>
                                <div className="p-5">
                                    {item.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>{item.category}</span>}
                                    <h3 className="font-bold text-base mt-2" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {visibleCount < filtered.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filtered.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({filtered.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? filtered.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Featured-grid variant ──
    if (variant === 'featured-grid') {
        const first = filteredItems[0];
        const rest = filteredItems.slice(1, visibleCount);
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Portfolio'} subtitle={content.subtitle} config={config} s={s} />
                    {/* Featured first item */}
                    {first && (
                        <div className={`${card} overflow-hidden group cursor-pointer mb-6 relative transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            onClick={() => first.link ? window.open(first.link, '_blank') : setLbIndex(0)}>
                            <div className="aspect-video overflow-hidden">
                                <MediaRenderer item={first} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-video" rounded="" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                                {first.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-white/90" style={{ backgroundColor: hexToRgba(s.accent, 0.7) }}>{first.category}</span>}
                                <h3 className="text-white font-bold text-xl md:text-3xl mt-2">{first.title}</h3>
                                {first.description && <p className="text-white/80 text-sm md:text-base mt-2 max-w-2xl">{first.description}</p>}
                                {first.link && <span className="inline-block mt-3 text-sm font-semibold text-white underline underline-offset-4">Ver proyecto →</span>}
                            </div>
                        </div>
                    )}
                    {/* Remaining items in 3-col grid */}
                    {rest.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {rest.map((item, i) => (
                                <div key={i} className={`${card} overflow-hidden group cursor-pointer transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}
                                    onClick={() => item.link ? window.open(item.link, '_blank') : setLbIndex(i + 1)}>
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                                    </div>
                                    <div className="p-5">
                                        {item.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>{item.category}</span>}
                                        <h3 className="font-bold text-base mt-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {visibleCount < filteredItems.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filteredItems.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({filteredItems.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? filteredItems.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Masonry variant ──
    if (variant === 'masonry') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Portfolio'} subtitle={content.subtitle} config={config} s={s} />
                    {categories.length > 1 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {categories.map(cat => (
                                <button key={cat} onClick={() => { setActiveFilter(cat); setVisibleCount(INITIAL_ITEMS); }}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === activeFilter ? 'text-white shadow-lg' : ''}`}
                                    style={cat === activeFilter ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>{cat}</button>
                            ))}
                        </div>
                    )}
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                        {filteredItems.slice(0, visibleCount).map((item, i) => {
                            return (
                                <div key={i} className={`${card} break-inside-avoid overflow-hidden group cursor-pointer transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}
                                    onClick={() => setLbIndex(i)}>
                                    {(item.resolvedImage || item.carouselImages || item.video) && <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="" rounded="" />}
                                    <div className="p-4">
                                        {item.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>{item.category}</span>}
                                        <h3 className="font-bold text-sm mt-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-xs mt-1" style={{ color: s.textSub }}>{item.description}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {visibleCount < filteredItems.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filteredItems.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({filteredItems.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? filteredItems.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Portfolio'} subtitle={content.subtitle} config={config} s={s} />
                    {categories.length > 1 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {categories.map(cat => (
                                <button key={cat} onClick={() => { setActiveFilter(cat); setVisibleCount(INITIAL_ITEMS); }}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === activeFilter ? 'text-white shadow-lg' : ''}`}
                                    style={cat === activeFilter ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>{cat}</button>
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredItems.slice(0, visibleCount).map((item, i) => {
                            return (
                                <div key={i} className={`${card} overflow-hidden group cursor-pointer transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}
                                    onClick={() => item.link ? window.open(item.link, '_blank') : setLbIndex(i)}>
                                    <div className="aspect-video overflow-hidden">
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-video" rounded="" />
                                    </div>
                                    <div className="p-5">
                                        {item.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>{item.category}</span>}
                                        <h3 className="font-bold text-base mt-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {visibleCount < filteredItems.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filteredItems.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({filteredItems.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? filteredItems.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Portfolio'} subtitle={content.subtitle} config={config} s={s} />
                {categories.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                        {categories.map(cat => (
                            <button key={cat} onClick={() => { setActiveFilter(cat); setVisibleCount(INITIAL_ITEMS); }}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === activeFilter ? 'text-white shadow-lg' : ''}`}
                                style={cat === activeFilter ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>{cat}</button>
                        ))}
                    </div>
                )}
                <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                    {filteredItems.slice(0, visibleCount).map((item, i) => {
                        return (
                            <div key={i} className={`${card} overflow-hidden group cursor-pointer transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${150 + Math.min(i, 10) * 100}ms` : '0ms' }}
                                onClick={() => item.link ? window.open(item.link, '_blank') : setLbIndex(i)}>
                                <div className="aspect-[4/3] overflow-hidden">
                                    <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                                </div>
                                <div className="p-5">
                                    {item.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>{item.category}</span>}
                                    <h3 className="font-bold text-base mt-2" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {visibleCount < filteredItems.length && (
                    <div className="text-center mt-8">
                        <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filteredItems.length))}
                            className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                            style={{borderColor: s.accent, color: s.accent}}>
                            Ver mas ({filteredItems.length - visibleCount} restantes)
                        </button>
                    </div>
                )}
            </div>
            <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                slides={filteredItems.filter(it => it.resolvedImage).map(it => ({ src: it.resolvedImage, alt: it.title || '' }))} />
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
